import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Button, Input } from '@img/ui';
import { ImageUploader } from '../../components/ImageUploader';
import { api, type ApiSuccess } from '../../lib/api';

type SettingsMap = Record<string, string | null>;

export function AdminSettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<ApiSuccess<SettingsMap>>('/api/settings'),
  });
  const [form, setForm] = useState<SettingsMap>({});
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [active, setActive] = useState<string>('site');

  useEffect(() => {
    if (data?.data) setForm(data.data);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (values: SettingsMap) => api.put('/api/settings', { values }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] });
      setSavedAt(new Date());
    },
  });

  if (isLoading) return <div className="text-gray-500">⟳ جاري التحميل…</div>;

  const get = (k: string, fallback = '') => form[k] ?? fallback;
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const setBool = (k: string, v: boolean) => setForm((f) => ({ ...f, [k]: v ? '1' : '0' }));
  const isBool = (k: string) => form[k] === '1' || form[k] === 'true';

  const isMaintenance = isBool('site_maintenance');

  const tabs = [
    { id: 'site',     label: 'الموقع',         icon: '🌐' },
    { id: 'hero',     label: 'الواجهة الرئيسية', icon: '🖼️' },
    { id: 'contact',  label: 'التواصل',        icon: '📞' },
    { id: 'social',   label: 'التواصل الاجتماعي', icon: '🔗' },
    { id: 'business', label: 'بيانات الشركة',  icon: '🏢' },
    { id: 'auctions', label: 'المزادات',       icon: '🔨' },
    { id: 'finance',  label: 'المالية',        icon: '💰' },
    { id: 'features', label: 'الميزات',        icon: '⚙️' },
    { id: 'seo',      label: 'SEO',            icon: '🔍' },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-black text-navy-dark">إعدادات الموقع</h1>
        <div className="flex items-center gap-3">
          {savedAt && <span className="text-xs text-green-600">✓ آخر حفظ: {savedAt.toLocaleTimeString('ar-SA')}</span>}
          <Button variant="primary" onClick={() => saveMutation.mutate(form)} loading={saveMutation.isPending}>
            💾 حفظ التغييرات
          </Button>
        </div>
      </div>

      {/* 🛠️ بانر صيانة */}
      <Card className="mb-6 border-2 border-amber-200 bg-amber-50">
        <CardBody>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-black text-lg text-amber-900 mb-1">🛠 وضع الصيانة</h2>
              <p className="text-sm text-amber-800">
                عند التفعيل، الموقع يعرض صفحة "تحت الصيانة" للزوار. الأدمن والمشرف يمرّون بشكل عادي.
              </p>
            </div>
            <Button
              variant={isMaintenance ? 'danger' : 'primary'}
              onClick={() => saveMutation.mutate({ site_maintenance: isMaintenance ? '0' : '1' })}
              loading={saveMutation.isPending}
            >
              {isMaintenance ? '⛔ إيقاف الصيانة' : '🛠 تفعيل الصيانة'}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActive(t.id)}
            className={`px-4 py-2 rounded-xl font-bold text-sm shrink-0 transition ${
              active === t.id ? 'bg-navy-dark text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <span className="ml-1">{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {active === 'site' && (
        <Section title="🌐 معلومات الموقع">
          <Input label="اسم الموقع (عربي)"     value={get('site_name_ar')}    onChange={set('site_name_ar')} />
          <Input label="اسم الموقع (إنجليزي)"   value={get('site_name_en')}    onChange={set('site_name_en')} />
          <Input label="الشعار/التاجلاين (عربي)" value={get('site_tagline_ar')} onChange={set('site_tagline_ar')} />
          <Input label="الشعار/التاجلاين (إنجليزي)" value={get('site_tagline_en')} onChange={set('site_tagline_en')} />
          <div>
            <ImageUploader
              value={get('site_logo_url')}
              onChange={(url) => {
                setForm((f) => ({ ...f, site_logo_url: url ?? '' }));
                saveMutation.mutate({ site_logo_url: url ?? '' });
              }}
              purpose="SITE_LOGO"
              label="🏷️ شعار الموقع (Logo)"
              hint="مربع — حد أقصى 2MB"
              shape="square"
              size="md"
            />
          </div>
          <div>
            <ImageUploader
              value={get('site_favicon')}
              onChange={(url) => {
                setForm((f) => ({ ...f, site_favicon: url ?? '' }));
                saveMutation.mutate({ site_favicon: url ?? '' });
              }}
              purpose="SITE_FAVICON"
              label="🔖 أيقونة التبويب (Favicon)"
              hint="32×32 أو 64×64 — حد أقصى 1MB"
              shape="square"
              size="sm"
            />
          </div>
          <TextArea label="نبذة قصيرة عن الموقع" value={get('site_description')} onChange={set('site_description')} />
        </Section>
      )}

      {active === 'hero' && (
        <HeroImagesManager
          value={get('hero_images')}
          onChange={(json) => {
            setForm((f) => ({ ...f, hero_images: json }));
            // حفظ تلقائي للصور (تجربة سريعة بدون الضغط على "حفظ")
            saveMutation.mutate({ hero_images: json });
          }}
        />
      )}

      {active === 'contact' && (
        <Section title="📞 معلومات التواصل">
          <Input label="رقم الهاتف الرئيسي"  value={get('contact_phone', '0114993666')}  onChange={set('contact_phone')} dir="ltr" />
          <Input label="رقم الواتساب"         value={get('contact_whatsapp', '966114993666')} onChange={set('contact_whatsapp')} dir="ltr" hint="بدون + بصيغة دولية" />
          <Input label="البريد الإلكتروني (عام)" type="email" value={get('contact_email', 'info@img.sa')} onChange={set('contact_email')} dir="ltr" />
          <Input label="البريد الإلكتروني (الدعم)" type="email" value={get('contact_support_email', 'support@img.sa')} onChange={set('contact_support_email')} dir="ltr" />
          <TextArea label="العنوان الكامل" value={get('contact_address')} onChange={set('contact_address')} />
          <Input label="ساعات العمل" value={get('contact_hours', 'السبت - الخميس 8 ص - 10 م')} onChange={set('contact_hours')} />
          <Input label="المدينة"   value={get('contact_city', 'الرياض')}  onChange={set('contact_city')} />
          <Input label="رابط الموقع على خرائط جوجل" value={get('contact_map_url')} onChange={set('contact_map_url')} dir="ltr" />
        </Section>
      )}

      {active === 'social' && (
        <Section title="🔗 روابط التواصل الاجتماعي">
          <Input label="X (تويتر سابقاً)" value={get('social_twitter')}  onChange={set('social_twitter')}  dir="ltr" placeholder="https://x.com/..." />
          <Input label="إنستغرام"          value={get('social_instagram')} onChange={set('social_instagram')} dir="ltr" />
          <Input label="سناب شات"           value={get('social_snapchat')} onChange={set('social_snapchat')} dir="ltr" />
          <Input label="تيك توك"             value={get('social_tiktok')}   onChange={set('social_tiktok')} dir="ltr" />
          <Input label="يوتيوب"             value={get('social_youtube')}  onChange={set('social_youtube')} dir="ltr" />
          <Input label="لينكد إن"           value={get('social_linkedin')} onChange={set('social_linkedin')} dir="ltr" />
          <Input label="فيسبوك"             value={get('social_facebook')} onChange={set('social_facebook')} dir="ltr" />
        </Section>
      )}

      {active === 'business' && (
        <Section title="🏢 بيانات الشركة الرسمية">
          <Input label="اسم الشركة الرسمي"        value={get('biz_legal_name', 'شركة صورة العقارية')} onChange={set('biz_legal_name')} />
          <Input label="رقم السجل التجاري"        value={get('biz_cr_number')}   onChange={set('biz_cr_number')} dir="ltr" />
          <Input label="الرقم الضريبي (VAT)"      value={get('biz_vat_number')}  onChange={set('biz_vat_number')} dir="ltr" />
          <Input label="رقم الترخيص العقاري"      value={get('biz_real_estate_license')} onChange={set('biz_real_estate_license')} dir="ltr" />
          <Input label="رقم رخصة فال (Fal)"       value={get('biz_fal_number')} onChange={set('biz_fal_number')} dir="ltr" />
          <Input label="رقم العضوية بهيئة العقار" value={get('biz_rega_number')} onChange={set('biz_rega_number')} dir="ltr" />
          <Input label="الرئيس التنفيذي / المدير"  value={get('biz_ceo_name')} onChange={set('biz_ceo_name')} />
        </Section>
      )}

      {active === 'auctions' && (
        <Section title="🔨 إعدادات المزادات الافتراضية">
          <Input label="نسبة السعي الافتراضية (%)"   type="number" step="0.1" value={get('auctions_default_commission', '2.5')} onChange={set('auctions_default_commission')} />
          <Input label="مقدار الزيادة الافتراضي (ر.س)" type="number" value={get('auctions_default_increment', '50000')} onChange={set('auctions_default_increment')} />
          <Input label="مبلغ التأمين الافتراضي (ر.س)"   type="number" value={get('auctions_default_deposit', '50000')} onChange={set('auctions_default_deposit')} />
          <Input label="ضريبة التصرفات العقارية (%)" type="number" step="0.1" value={get('auctions_property_tax', '5')} onChange={set('auctions_property_tax')} />
          <Input label="مدة Anti-sniping (ثواني)"   type="number" value={get('auctions_anti_sniping', '60')} onChange={set('auctions_anti_sniping')} />
          <Input label="مدة عداد الحراج (ثواني)"     type="number" value={get('auctions_stage_cooldown', '5')} onChange={set('auctions_stage_cooldown')} />
          <Toggle label="تمديد المزاد عند مزايدة في آخر دقيقة (افتراضي)" on={isBool('auctions_default_anti_sniping_enabled') || form.auctions_default_anti_sniping_enabled === undefined} onToggle={(v) => setBool('auctions_default_anti_sniping_enabled', v)} />
          <Toggle label="السماح للزوار بالمشاهدة (افتراضي)" on={isBool('auctions_default_allow_guests') || form.auctions_default_allow_guests === undefined} onToggle={(v) => setBool('auctions_default_allow_guests', v)} />
        </Section>
      )}

      {active === 'finance' && (
        <Section title="💰 الإعدادات المالية">
          <Input label="عملة الموقع (رمز)" value={get('finance_currency', 'ر.س')} onChange={set('finance_currency')} />
          <Input label="عملة (ISO)"         value={get('finance_currency_iso', 'SAR')} onChange={set('finance_currency_iso')} dir="ltr" />
          <Input label="نسبة الـ VAT (%)" type="number" step="0.1" value={get('finance_vat_rate', '15')} onChange={set('finance_vat_rate')} />
          <Input label="حد أدنى للإيداع (ر.س)" type="number" value={get('finance_min_deposit', '1000')} onChange={set('finance_min_deposit')} />
          <Input label="حد أقصى للإيداع (ر.س)" type="number" value={get('finance_max_deposit', '500000')} onChange={set('finance_max_deposit')} />
          <Input label="حساب IBAN للتحويلات" value={get('finance_iban')} onChange={set('finance_iban')} dir="ltr" />
          <Input label="اسم البنك" value={get('finance_bank_name')} onChange={set('finance_bank_name')} />
          <Input label="اسم صاحب الحساب" value={get('finance_account_holder')} onChange={set('finance_account_holder')} />
        </Section>
      )}

      {active === 'features' && (
        <Section title="⚙️ تفعيل/إيقاف الميزات">
          <Toggle label="السماح بالتسجيل العام"        on={isBool('feature_signup') || form.feature_signup === undefined} onToggle={(v) => setBool('feature_signup', v)} />
          <Toggle label="السماح بطلبات إقامة المزاد" on={isBool('feature_auction_requests') || form.feature_auction_requests === undefined} onToggle={(v) => setBool('feature_auction_requests', v)} />
          <Toggle label="السماح بإضافة عقار من المستخدم" on={isBool('feature_user_add_property')} onToggle={(v) => setBool('feature_user_add_property', v)} />
          <Toggle label="عرض المسوّقين"                 on={isBool('feature_brokers') || form.feature_brokers === undefined} onToggle={(v) => setBool('feature_brokers', v)} />
          <Toggle label="نظام المحفظة الإلكترونية"      on={isBool('feature_wallet') || form.feature_wallet === undefined} onToggle={(v) => setBool('feature_wallet', v)} />
          <Toggle label="إدارة اتحادات الملاك"          on={isBool('feature_owners_unions')} onToggle={(v) => setBool('feature_owners_unions', v)} />
          <Toggle label="نظام الإشعارات للمستخدمين"     on={isBool('feature_notifications') || form.feature_notifications === undefined} onToggle={(v) => setBool('feature_notifications', v)} />
        </Section>
      )}

      {active === 'seo' && (
        <Section title="🔍 إعدادات SEO وميتا تاغز">
          <Input label="Meta Title (عربي)"   value={get('seo_meta_title_ar', 'شركة صورة العقارية')} onChange={set('seo_meta_title_ar')} />
          <Input label="Meta Title (إنجليزي)" value={get('seo_meta_title_en', 'Sora Real Estate')}  onChange={set('seo_meta_title_en')} dir="ltr" />
          <TextArea label="Meta Description (عربي)"   value={get('seo_meta_desc_ar')} onChange={set('seo_meta_desc_ar')} hint="160 حرف كحد أقصى" />
          <TextArea label="Meta Description (إنجليزي)" value={get('seo_meta_desc_en')} onChange={set('seo_meta_desc_en')} dir="ltr" hint="160 chars max" />
          <Input label="Meta Keywords (عربي)"   value={get('seo_meta_keywords_ar')} onChange={set('seo_meta_keywords_ar')} hint="مفصولة بفواصل" />
          <Input label="Meta Keywords (إنجليزي)" value={get('seo_meta_keywords_en')} onChange={set('seo_meta_keywords_en')} dir="ltr" />
          <div>
            <ImageUploader
              value={get('seo_og_image')}
              onChange={(url) => {
                setForm((f) => ({ ...f, seo_og_image: url ?? '' }));
                saveMutation.mutate({ seo_og_image: url ?? '' });
              }}
              purpose="SEO_OG_IMAGE"
              label="🌐 صورة المشاركة الاجتماعية (OG Image)"
              hint="عريضة 1200×630 — حد أقصى 5MB"
              shape="wide"
            />
          </div>
          <Input label="Google Analytics ID"    value={get('seo_ga_id')} onChange={set('seo_ga_id')} dir="ltr" placeholder="G-XXXXXXXXXX" />
          <Input label="Google Search Console" value={get('seo_gsc_verification')} onChange={set('seo_gsc_verification')} dir="ltr" hint="رمز التحقق" />
          <Input label="Tag Manager ID"        value={get('seo_gtm_id')} onChange={set('seo_gtm_id')} dir="ltr" />
        </Section>
      )}

      {/* فوتر زر الحفظ */}
      <div className="flex items-center justify-end gap-3 mt-6 sticky bottom-4 bg-white p-4 rounded-2xl shadow-lg border border-gray-200 z-10">
        {savedAt && <span className="text-xs text-green-600">✓ تم الحفظ ({savedAt.toLocaleTimeString('ar-SA')})</span>}
        <Button variant="primary" size="lg" onClick={() => saveMutation.mutate(form)} loading={saveMutation.isPending}>
          💾 حفظ التغييرات
        </Button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="mb-6">
      <CardBody>
        <h2 className="font-black text-lg text-navy-dark mb-4 border-b-2 border-gold pb-2 inline-block">{title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
      </CardBody>
    </Card>
  );
}

function Toggle({ label, on, onToggle }: { label: string; on: boolean; onToggle: (v: boolean) => void }) {
  return (
    <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition md:col-span-2 ${
      on ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white hover:bg-gray-50'
    }`}>
      <input type="checkbox" checked={on} onChange={(e) => onToggle(e.target.checked)} className="w-5 h-5 accent-green-600" />
      <span className="text-sm font-bold text-ink flex-1">{label}</span>
    </label>
  );
}

function TextArea({ label, value, onChange, hint, dir }: { label: string; value: string; onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void; hint?: string; dir?: string }) {
  return (
    <div className="md:col-span-2">
      <label className="block text-sm font-bold text-ink mb-1">{label}</label>
      <textarea
        value={value}
        onChange={onChange}
        rows={3}
        dir={dir}
        className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white"
      />
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

/* ━━━━━━━━━━━━━━━━━━ HERO IMAGES MANAGER ━━━━━━━━━━━━━━━━━━ */
/**
 * إدارة صور الواجهة الرئيسية (Hero):
 * - رفع متعدد للصور
 * - إعادة ترتيب (سهم أعلى/أسفل)
 * - حذف صورة بتأكيد
 * - معاينة الكاروسيل
 * - يحفظ JSON array من الـ URLs في settings.hero_images
 */
function HeroImagesManager({ value, onChange }: { value: string; onChange: (json: string) => void }) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const images = (() => {
    try {
      const arr = JSON.parse(value || '[]');
      return Array.isArray(arr) ? (arr as string[]) : [];
    } catch {
      return [] as string[];
    }
  })();

  const update = (next: string[]) => onChange(JSON.stringify(next));

  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= images.length) return;
    const next = [...images];
    [next[idx], next[j]] = [next[j], next[idx]];
    update(next);
  };

  const remove = (idx: number) => {
    if (!confirm('حذف هذه الصورة من الواجهة الرئيسية؟')) return;
    update(images.filter((_, i) => i !== idx));
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setUploading(true);
    setUploadError(null);
    try {
      const base =
        (import.meta as { env: { VITE_API_URL?: string; DEV?: boolean } }).env.VITE_API_URL ||
        ((import.meta as { env: { DEV?: boolean } }).env.DEV
          ? 'http://localhost:3000'
          : (typeof window !== 'undefined' ? window.location.origin : ''));
      const next = [...images];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('purpose', 'OTHER');
        const res = await fetch(`${base}/api/uploads`, { method: 'POST', body: fd, credentials: 'include' });
        const json = (await res.json()) as { success?: boolean; data?: { url: string }; error?: string };
        if (!json.success || !json.data?.url) throw new Error(json.error ?? 'فشل رفع الصورة');
        next.push(json.data.url.startsWith('http') ? json.data.url : `${base}${json.data.url}`);
      }
      update(next);
    } catch (err) {
      setUploadError((err as Error).message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <Card className="mb-6">
      <CardBody>
        <h2 className="font-black text-lg text-navy-dark mb-1 border-b-2 border-gold pb-2 inline-block">
          🖼️ صور الواجهة الرئيسية
        </h2>
        <p className="text-xs text-ink-muted mb-5 mt-2 leading-relaxed">
          الصور تظهر ككاروسيل دوّار في رأس الصفحة الرئيسية. الترتيب من أعلى لأسفل = ترتيب الكاروسيل. يتم التبديل تلقائياً كل 5 ثوانٍ.
        </p>

        {/* زرّ الرفع */}
        <div className="bg-navy-soft/40 border-2 border-dashed border-navy/20 rounded-xl p-6 text-center mb-5">
          <div className="text-4xl mb-2">📤</div>
          <h3 className="font-black text-navy-dark mb-1">ارفع صور الواجهة</h3>
          <p className="text-xs text-ink-muted mb-3">يفضّل صور بمقاس عريض (1200×800 أو أعلى) — JPG/PNG/WebP</p>
          <label className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-navy-dark hover:bg-navy text-white text-sm font-bold cursor-pointer transition">
            <input type="file" accept="image/*" multiple onChange={handleFileSelect} className="hidden" disabled={uploading} />
            {uploading ? '⟳ جاري الرفع…' : '📎 اختر صور'}
          </label>
          {uploadError && (
            <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
              ⚠️ {uploadError}
            </div>
          )}
        </div>

        {/* القائمة الحالية */}
        {images.length === 0 ? (
          <div className="text-center py-12 text-ink-muted">
            <div className="text-5xl mb-2 opacity-40">🖼️</div>
            <p className="text-sm">لا توجد صور — سيتم استخدام الصور الافتراضية</p>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-navy-dark text-sm">{images.length} صورة في الكاروسيل</h3>
              <button
                onClick={() => {
                  if (confirm('حذف جميع الصور والعودة للصور الافتراضية؟')) update([]);
                }}
                className="text-xs text-red-600 hover:underline"
              >
                🗑️ حذف الكل
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {images.map((url, idx) => (
                <div key={url + idx} className="relative bg-white border-2 border-gray-200 rounded-xl overflow-hidden group">
                  <div className="aspect-[4/3] bg-gray-100">
                    <img src={url} alt="" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }} />
                  </div>
                  <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-bold">
                    #{idx + 1}
                  </div>
                  <div className="p-2 flex items-center gap-1.5 bg-gray-50 border-t border-gray-200">
                    <button
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      className="p-1.5 rounded-lg border border-gray-200 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
                      title="نقل لأعلى"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => move(idx, 1)}
                      disabled={idx === images.length - 1}
                      className="p-1.5 rounded-lg border border-gray-200 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
                      title="نقل لأسفل"
                    >
                      ↓
                    </button>
                    <span className="flex-1 text-[10px] text-ink-muted truncate" dir="ltr" title={url}>{url.split('/').pop()}</span>
                    <button
                      onClick={() => remove(idx)}
                      className="p-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                      title="حذف"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* معاينة */}
            <div className="mt-6 bg-navy-soft/30 rounded-xl p-4">
              <div className="text-xs font-bold text-navy-dark mb-2">👁️ معاينة الترتيب:</div>
              <div className="flex gap-1.5 overflow-x-auto pb-2">
                {images.map((url, idx) => (
                  <div key={idx} className="shrink-0 w-20 h-14 rounded-lg overflow-hidden ring-1 ring-gray-200 relative">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <span className="absolute bottom-0.5 right-0.5 bg-black/60 text-white text-[9px] px-1 rounded">{idx + 1}</span>
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-ink-muted mt-1">سيتم التبديل بين هذه الصور بترتيبها كل 5 ثوانٍ.</div>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
